import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { Button, Separator } from '@/components/ui'
import { useSEO, createArticleSchema } from '@/hooks/useSEO'
import { useScrollReveal, useHeroReveal } from '@gjl/motion/css'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'
import { BeforeAfterSlider } from '@/components/BeforeAfterSlider'

export default function WendellWhalumCaseStudyPage() {
  const breadcrumbs = generateBreadcrumbs('/blog/wendell-whalum-case-study', breadcrumbLabels)

  const heroRef = useHeroReveal<HTMLElement>(0.2)
  const sectionsRef = useScrollReveal<HTMLElement>('.article-section', { stagger: 0.1 })

  useSEO({
    title: 'A Choir That Sang for Presidents, Hidden Behind “Under Construction”',
    description:
      'A redesign concept for the Wendell P. Whalum Community Chorus: a sixty-year Atlanta choir that sang for Carter, Mandela, and Carnegie Hall, with all of it buried behind a “Site Under Construction” page. We surfaced the history. Built from their own public content.',
    url: '/blog/wendell-whalum-case-study',
    canonical: '/blog/wendell-whalum-case-study',
    type: 'article',
    breadcrumbs,
    article: {
      publishedTime: '2026-06-24',
      author: 'Garrett John Law',
      section: 'Case Studies',
      tags: ['website redesign', 'community choir', 'godaddy', 'the full score', 'choral music'],
    },
    schema: createArticleSchema({
      headline: 'A Choir That Sang for Presidents, Hidden Behind “Under Construction”',
      description:
        'A redesign concept for the Wendell P. Whalum Community Chorus, surfacing a sixty-year Atlanta choral legacy from behind a “Site Under Construction” page.',
      datePublished: '2026-06-24',
      url: '/blog/wendell-whalum-case-study',
    }),
  })

  return (
    <article ref={sectionsRef} className="mx-auto max-w-3xl space-y-12 px-4 py-16">
      {/* Header */}
      <header ref={heroRef} className="space-y-4">
        <Link to="/blog" className="text-primary text-sm hover:underline">
          &larr; Back to Blog
        </Link>
        <h1 className="text-fluid-h2">A Choir That Sang for Presidents, Hidden Behind “Under Construction”</h1>
        <div className="text-muted-foreground flex items-center gap-4 text-sm">
          <time dateTime="2026-06-24">June 24, 2026</time>
          <span>&middot;</span>
          <span>4 min read</span>
        </div>
        <p className="text-fluid-h4 text-muted-foreground">
          The Wendell P. Whalum Community Chorus has sung at a presidential inauguration, for Nelson Mandela, at the
          1996 Olympics, and at Carnegie Hall. None of that reached anyone who looked the chorus up online, because the
          front door said the site was under construction. So we rebuilt it around the history.
        </p>
      </header>

      {/* Cover: drag-to-compare hero */}
      <figure className="not-prose">
        <BeforeAfterSlider
          beforeSrc="/images/blog/wendell-whalum/before-home.webp"
          afterSrc="/images/blog/wendell-whalum/after-home.webp"
          beforeAlt="The original Wendell P. Whalum Community Chorus homepage on GoDaddy: a stock template with a pink box reading Site Under Construction, please check back in a few days"
          afterAlt="The rebuilt homepage: the full chorus in concert dress behind a warm scrim, the line Offering you the joy of music in gold, and a button to the performances"
          beforeLabel="Under Construction"
          afterLabel="Rebuilt"
        />
        <figcaption className="text-muted-foreground mt-3 text-sm">
          Drag the handle to compare: the live site and the rebuilt demo, same screen. We built the redesign from the
          chorus&rsquo;s own public history &mdash; it isn&rsquo;t a client engagement, it&rsquo;s a working demo of
          what the site could be.
        </figcaption>
      </figure>

      <Separator />

      {/* The Goal */}
      <section className="prose prose-invert article-section max-w-none space-y-4">
        <h2 className="text-fluid-h2">A Community Choir&rsquo;s Website Has Two Jobs</h2>
        <p>
          The concerts are free, so the website is not a ticket page. It has two jobs: bring people into the seats, and
          bring the next singer onto the risers. For a chorus with sixty years behind it, the way you do both is the
          same &mdash; you let people feel the <strong>weight of the history</strong> the moment they arrive.
        </p>
        <p>
          The Whalum Chorus has that history in abundance. Founded in 1966 at the Atlanta University Center and led
          today by Dr. David Morrow of the Morehouse College Glee Club, it sang at President Carter&rsquo;s
          inauguration, for Nelson Mandela, in the 1996 Olympic Cultural Olympiad, and at Carnegie Hall. The trouble was
          that none of it was doing any work for them online.
        </p>
      </section>

      {/* Visual: the rebuilt home, early */}
      <figure className="not-prose article-section space-y-2">
        <div className="border-border h-[28rem] overflow-y-auto rounded-lg border">
          <img
            src="/images/blog/wendell-whalum/home-full.webp"
            alt="The full rebuilt homepage, top to bottom: the cinematic choir hero, the Heritage Timeline from 1966 to Carnegie Hall, the performances, and the director feature"
            width={1200}
            height={5848}
            loading="lazy"
            className="w-full"
          />
        </div>
        <figcaption className="text-muted-foreground text-sm">
          The rebuilt homepage, top to bottom. The history is the centerpiece, not an inner page nobody reaches.
        </figcaption>
      </figure>

      <Separator />

      {/* The Problem */}
      <section className="prose prose-invert article-section max-w-none space-y-4">
        <h2 className="text-fluid-h2">What the Old Site Was Costing Them</h2>
        <p>
          The site ran on a generic GoDaddy template, and its homepage opened on a &ldquo;Site Under Construction&rdquo;
          notice telling visitors to check back in a few days. We graded it against{' '}
          <Link to="/how-we-measure" className="text-primary hover:underline">
            The Full Score
          </Link>
          , our six-pillar standard. It came back a <strong>58 &mdash; an F.</strong> The gaps were not cosmetic:
        </p>
        <ul className="list-disc space-y-2 pl-6">
          <li>
            The <strong>homepage was a placeholder.</strong> Anyone who searched for the chorus, or followed a link to
            it, met a &ldquo;check back in a few days&rdquo; page and left.
          </li>
          <li>
            The remarkable history existed, but it was <strong>buried on an inner page</strong> behind that notice, so
            almost no visitor ever reached the story of Carter, Mandela, the Olympics, or Carnegie Hall.
          </li>
          <li>
            Measured the way a screen reader reads a page, the site scored <strong>0 for accessibility</strong> &mdash;
            a blind or low-vision visitor got essentially nothing.
          </li>
          <li>
            There was no next concert to find and no clear way to sing with the chorus, so the two jobs the site existed
            to do went undone.
          </li>
        </ul>
      </section>

      <Separator />

      {/* The Build */}
      <section className="prose prose-invert article-section max-w-none space-y-4">
        <h2 className="text-fluid-h2">The Rebuild</h2>
        <p>
          We kept every real fact &mdash; the founding, the director, the verbatim history &mdash; and rebuilt the
          experience around it. The design is a dark, dignified &ldquo;heritage program&rdquo;: a deep espresso and
          antique gold, a high-contrast serif, and a cinematic hero on the full chorus, with the chorus&rsquo;s own
          line, <em>offering you the joy of music</em>, set in gold.
        </p>
        <p>
          The centerpiece is a <strong>Heritage Timeline</strong> &mdash; a gold-spined chronology that carries a reader
          from the 1966 founding through Carter, Mandela, the Olympics, and Carnegie Hall. The thing that makes this
          chorus singular is now the first thing a visitor feels.
        </p>
      </section>

      {/* Visual: the timeline */}
      <figure className="not-prose article-section space-y-2">
        <img
          src="/images/blog/wendell-whalum/timeline.webp"
          alt="The Heritage Timeline section: a gold-spined chronology of the chorus's milestones from 1966 to Carnegie Hall"
          width={1366}
          height={860}
          loading="lazy"
          className="border-border w-full rounded-lg border"
        />
        <figcaption className="text-muted-foreground text-sm">
          The Heritage Timeline &mdash; sixty years of singing, finally on the front of the house.
        </figcaption>
      </figure>

      <Separator />

      {/* The Result */}
      <section className="prose prose-invert article-section max-w-none space-y-4">
        <h2 className="text-fluid-h2">The Difference, Measured</h2>
        <p>
          We ran the rebuild back through the same Full Score checker. The site went from a <strong>58 to a 98</strong>{' '}
          &mdash; an F to an A &mdash; with accessibility, search, and the phone experience at or near perfect, and the
          history finally visible to anyone who lands on the page.
        </p>

        {/* Metrics */}
        <div className="my-8 grid gap-6 text-center md:grid-cols-3">
          <div className="bg-muted/10 border-border rounded-lg border p-4">
            <p className="text-primary text-2xl font-bold">58 &rarr; 98</p>
            <p className="text-muted-foreground text-sm">Full Score, an F to an A</p>
          </div>
          <div className="bg-muted/10 border-border rounded-lg border p-4">
            <p className="text-primary text-2xl font-bold">0 &rarr; 100</p>
            <p className="text-muted-foreground text-sm">Accessibility &mdash; from the floor to perfect</p>
          </div>
          <div className="bg-muted/10 border-border rounded-lg border p-4">
            <p className="text-primary text-2xl font-bold">Placeholder &rarr; live</p>
            <p className="text-muted-foreground text-sm">The front door shows the chorus again</p>
          </div>
        </div>

        <p>
          We hold our own work to the same standard. The checker flagged a real contrast problem on our first pass
          &mdash; the lede over the photo was too faint &mdash; so we fixed it. That is the whole point of grading
          against a standard: it catches the gaps on our sites as readily as on anyone else&rsquo;s.
        </p>
      </section>

      <Separator />

      {/* Lessons */}
      <section className="prose prose-invert article-section max-w-none space-y-4">
        <h2 className="text-fluid-h2">What This Means for Your Choir</h2>
        <p>
          Most choirs are not short on history or talent. They are short on a website that shows it. The story that
          would make someone want to come, or want to sing, ends up on an inner page nobody reaches, behind a homepage
          that is dated or, worse, says the site is not ready yet.
        </p>
        <p>
          The fix is not a new coat of paint. It is deciding what the site is for &mdash; filling the seats and the
          risers &mdash; and putting the thing that does that, your history and your music, on the front of the house.
          We grade honestly against a standard, fix the gaps that matter, and let the legacy be seen.
        </p>
      </section>

      <Separator />

      {/* CTA */}
      <section className="bg-primary/5 border-primary/20 article-section space-y-6 rounded-lg border p-8 text-center">
        <h3 className="text-2xl font-semibold">Want to See Your Own Full Score?</h3>
        <p className="text-muted-foreground mx-auto max-w-2xl">
          We grade your current site against the same six pillars and hand you the list of gaps, whether or not you ever
          hire us. If the gaps are worth fixing, we can rebuild it the way we did for the Whalum Chorus.
        </p>
        <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
          <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 400, damping: 25 }}
          >
            <Link to="/contact">
              <Button variant="default" size="lg" className="cursor-pointer">
                Get Your Full Score &rarr;
              </Button>
            </Link>
          </motion.div>
          <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 400, damping: 25 }}
          >
            <Link to="/how-we-measure">
              <Button variant="outline" size="lg" className="cursor-pointer">
                How We Measure
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      <Separator />

      {/* Author Bio */}
      <section className="border-border border-t pt-8">
        <div className="flex items-start gap-4">
          <div className="flex-1">
            <p className="text-lg font-semibold">About the Author</p>
            <p className="text-muted-foreground mt-2">
              <strong>Garrett John Law</strong> is a software engineer and musician who builds digital platforms for
              festivals, schools, and arts organizations. He&rsquo;s an Interlochen alum and CIM graduate, and the
              founder of Fugue Lab, where he rebuilds the websites music organizations rely on to fill the room.
            </p>
            <div className="mt-4">
              <Link to="/about" className="text-primary text-sm hover:underline">
                Learn more about Fugue Lab &rarr;
              </Link>
            </div>
          </div>
        </div>
      </section>
    </article>
  )
}
